import { Fragment, useMemo, useState, type FormEvent } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getPayrollReport } from '../api/endpoints/payroll';
import { downloadReportExport } from '../api/endpoints/reports';
import type { PayrollReport as Payroll, PayrollRow } from '../api/schemas';
import { formatMoneyCents } from '../lib/format';
import {
  cutoffLabel,
  defaultPayrollChoice,
  formatMinutes,
  monthName,
  PAYROLL_HALVES,
  payrollPeriod,
  payrollRangeProblem,
  rateLabel,
  type PayrollHalf,
  type PayrollMonth,
} from '../lib/payroll';
import { periodLabel, type ReportPeriod } from '../lib/reports';
import { ErrorBanner } from './ErrorBanner';
import { LoadingState } from './LoadingState';
import { PayRateEditor } from './PayRateEditor';

type HalfChoice = PayrollHalf | 'fechas';

const MONTHS: readonly PayrollMonth[] = ['anterior', 'actual'];
const LOAD_FAILED = 'No pudimos armar la planilla. Revisa tu conexión e intenta de nuevo.';

/**
 * Finanzas > Reportes > Planilla: what each coach is owed for a quincena, by
 * the rate on file, with its cut-off date. The rate is edited right in the
 * row; the CSV is the same period for the contador.
 */
export function PayrollReport() {
  const [initial] = useState(() => defaultPayrollChoice(new Date()));
  const [half, setHalf] = useState<HalfChoice>(initial.half);
  const [month, setMonth] = useState<PayrollMonth>(initial.month);
  const [custom, setCustom] = useState<ReportPeriod>(() => payrollPeriod(initial.half, initial.month, new Date()));
  const [draft, setDraft] = useState<ReportPeriod>(custom);
  const [draftError, setDraftError] = useState<string>();

  const period = useMemo(
    () => (half === 'fechas' ? custom : payrollPeriod(half, month, new Date())),
    [half, month, custom],
  );
  const payrollQuery = useQuery({
    queryKey: ['business-payroll', period.from, period.to],
    queryFn: () => getPayrollReport(period),
    placeholderData: keepPreviousData,
  });
  const payroll = payrollQuery.data;

  const applyDates = (event: FormEvent) => {
    event.preventDefault();
    const problem = payrollRangeProblem(draft);
    setDraftError(problem);
    if (!problem) setCustom(draft);
  };

  return (
    <section className="section report-section payroll" aria-label="Planilla">
      <header className="report-section-head">
        <h2 className="section-label">Planilla</h2>
        <p className="row-sub">Lo que se le paga a cada coach por las clases que dio.</p>
      </header>

      <div className="report-period payroll-period">
        <div className="chip-row" role="group" aria-label="Quincena">
          {PAYROLL_HALVES.map((option) => (
            <button
              key={option.id}
              type="button"
              className="chip"
              data-status={half === option.id ? 'active' : undefined}
              onClick={() => setHalf(option.id)}
            >
              {option.label}
            </button>
          ))}
          <button
            type="button"
            className="chip"
            data-status={half === 'fechas' ? 'active' : undefined}
            onClick={() => {
              setDraft(period);
              setHalf('fechas');
            }}
          >
            Otro período
          </button>
        </div>
        {half === 'fechas' ? (
          <form onSubmit={applyDates} aria-label="Fechas de la planilla" className="filter-row report-dates">
            <div className="field">
              <label className="field-label" htmlFor="planilla-desde">
                Desde
              </label>
              <input
                id="planilla-desde"
                type="date"
                value={draft.from}
                onChange={(event) => setDraft({ ...draft, from: event.target.value })}
              />
            </div>
            <div className="field">
              <label className="field-label" htmlFor="planilla-hasta">
                Hasta
              </label>
              <input
                id="planilla-hasta"
                type="date"
                value={draft.to}
                onChange={(event) => setDraft({ ...draft, to: event.target.value })}
              />
            </div>
            <button type="submit" className="btn small secondary">
              Ver
            </button>
            {draftError ? <p className="field-error">{draftError}</p> : null}
          </form>
        ) : (
          <div className="seg" role="group" aria-label="Mes">
            {MONTHS.map((option) => (
              <button key={option} type="button" aria-pressed={month === option} onClick={() => setMonth(option)}>
                {monthName(option, new Date())}
              </button>
            ))}
          </div>
        )}
      </div>

      {payrollQuery.isLoading ? <LoadingState label="Armando la planilla…" /> : null}
      {payrollQuery.isError ? (
        <div className="report-error">
          <ErrorBanner message={payrollQuery.error instanceof ApiError ? payrollQuery.error.message : LOAD_FAILED} />
          <button type="button" className="btn small secondary" onClick={() => void payrollQuery.refetch()}>
            Reintentar
          </button>
        </div>
      ) : null}
      {payroll ? <PayrollBody payroll={payroll} period={period} stale={payrollQuery.isPlaceholderData} /> : null}
    </section>
  );
}

function PayrollBody({ payroll, period, stale }: { payroll: Payroll; period: ReportPeriod; stale: boolean }) {
  const missing = payroll.rows.filter((row) => row.rateCents === null).length;
  return (
    <div className="payroll-body" data-stale={stale || undefined}>
      <p className="payroll-cutoff">{cutoffLabel(payroll.cutoff)}</p>
      <p className="row-sub">
        Clases dictadas del {periodLabel(period)}. No cuentan las canceladas ni las que aún no empiezan.
      </p>
      {missing > 0 ? (
        <p className="row-sub payroll-missing">
          {missing === 1 ? '1 persona sin tarifa' : `${missing} personas sin tarifa`}: su total sale en $0.00 hasta que
          le pongas una.
        </p>
      ) : null}
      {payroll.rows.length === 0 ? (
        <p className="report-empty">Todavía no hay nadie en tu equipo.</p>
      ) : (
        <PayrollTable payroll={payroll} />
      )}
      <PayrollCsv period={period} />
    </div>
  );
}

function PayrollTable({ payroll }: { payroll: Payroll }) {
  const [editing, setEditing] = useState<string | null>(null);
  const classes = payroll.rows.reduce((total, row) => total + row.classes, 0);
  const minutes = payroll.rows.reduce((total, row) => total + row.minutes, 0);
  return (
    <div className="table-scroll payroll-table">
      <table>
        <thead>
          <tr>
            <th scope="col">Coach</th>
            <th scope="col" className="num">
              Clases
            </th>
            <th scope="col" className="num">
              Horas
            </th>
            <th scope="col">Tarifa</th>
            <th scope="col" className="num">
              A pagar
            </th>
          </tr>
        </thead>
        <tbody>
          {payroll.rows.map((row) => (
            <Fragment key={row.staffId}>
              <tr>
                <th scope="row">{row.name}</th>
                <td className="num payroll-classes" data-unit={row.classes === 1 ? 'clase' : 'clases'}>
                  {row.classes}
                </td>
                <td className="num payroll-minutes">{formatMinutes(row.minutes)}</td>
                <td className="payroll-rate-cell">
                  <RateCell row={row} onEdit={() => setEditing(row.staffId)} />
                </td>
                <td className="num amount payroll-amount">{formatMoneyCents(row.totalCents)}</td>
              </tr>
              {editing === row.staffId ? (
                <tr className="payroll-edit-row">
                  <td colSpan={5}>
                    <PayRateEditor row={row} onDone={() => setEditing(null)} />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td className="num payroll-classes" data-unit={classes === 1 ? 'clase' : 'clases'}>
              {classes}
            </td>
            <td className="num payroll-minutes">{formatMinutes(minutes)}</td>
            <td className="payroll-rate-cell" />
            <td className="num amount payroll-amount">{formatMoneyCents(payroll.totalCents)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function RateCell({ row, onEdit }: { row: PayrollRow; onEdit: () => void }) {
  if (row.rateCents === null) {
    return (
      <span className="payroll-rate">
        <span className="status" data-tone="hold">
          Sin tarifa
        </span>
        <button type="button" className="btn small secondary" aria-label={`Poner tarifa a ${row.name}`} onClick={onEdit}>
          Poner tarifa
        </button>
      </span>
    );
  }
  return (
    <span className="payroll-rate">
      <span>{rateLabel(row.rateCents, row.basis)}</span>
      <button type="button" className="btn small ghost" aria-label={`Cambiar la tarifa de ${row.name}`} onClick={onEdit}>
        Cambiar
      </button>
    </span>
  );
}

function PayrollCsv({ period }: { period: ReportPeriod }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const download = async () => {
    setBusy(true);
    setFailed(false);
    try {
      await downloadReportExport('planilla', period);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="payroll-csv">
      <button type="button" className="btn small secondary" disabled={busy} onClick={() => void download()}>
        {busy ? 'Preparando…' : 'Descargar CSV'}
      </button>
      {failed ? <p className="row-sub">No se pudo descargar. Intenta de nuevo.</p> : null}
    </div>
  );
}
